import { Map } from 'immutable';
import { v4 } from 'uuid';

import { createController } from '../utils/controller';
import { keysToCamelCase } from '../utils/string';
import { formatAmount } from '../utils/affiliates';
import { isTruthy } from '../utils/bool';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { APPLY_STATUS } from '../constants/affiliates';
import { EMPTY } from '../constants';
import { MODALS } from '../constants/modal';

import actions from '../actions';

const creditsController = createController();
const historyController = createController();
const linkController = createController();
const payoutController = createController();

const EMPTY_STATE = Map({
  link: '',
  history: EMPTY.LIST,
  payout: Map({
    availablePayoutAmount: 0,
    milestones: EMPTY.LIST,
    totalPayoutAmount: 0,
  }),
});

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    creditsController,
    historyController,
    linkController,
    payoutController,
  ].forEach(abort);
}

function* bootstrap(reduction) {
  abortAll();

  switch (reduction.getIn(['user', 'affiliateStatus'])) {
    case APPLY_STATUS.ACTIVE:
    case APPLY_STATUS.SUBMITTED: {
      yield function effect(dispatch) {
        dispatch(actions.affiliate.getHistory());
        dispatch(actions.affiliate.getLink());
        dispatch(actions.affiliate.getPayout());
      };

      return reduction;
    }

    case APPLY_STATUS.REJECTED: {
      return reduction.set('affiliate', EMPTY_STATE);
    }

    default: {
      return reduction.set('affiliate', EMPTY_STATE.set('modal', Map({ type: MODALS.AFFILIATE.SURVEY.APPLY })));
    }
  }
}

function* cleanUp(reduction) {
  abortAll();
  return reduction.set('affiliate', EMPTY.MAP);
}

function* getHistory(reduction) {
  yield function effect(dispatch) {
    const { signal } = historyController.start();

    function onSuccess(response) {
      historyController.stop();
      dispatch(actions.affiliate.getHistory.fulfilled(response));
    }

    function onFail(error) {
      if (signal.aborted) return;

      historyController.stop();
      dispatch(actions.affiliate.getHistory.rejected(error));
    }

    api.affiliates
      .getHistory()
      .then(onSuccess, onFail);
  };

  return reduction;
}

function* getHistoryFulfilled(reduction, response) {
  const formatters = {
    amount(value) {
      return value && formatAmount(value);
    },
  };

  function reduceData(history, item) {
    return history.push(keysToCamelCase({ data: item, formatters, toImmutable: true }).set('key', v4()));
  }

  return reduction.setIn(['affiliate', 'history'], response.data.reduce(reduceData, EMPTY.LIST));
}

function* getHistoryRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* getLink(reduction) {
  yield function effect(dispatch) {
    const { signal } = linkController.start();

    function onSuccess(response) {
      linkController.stop();
      dispatch(actions.affiliate.getLink.fulfilled(response));
    }

    function onFail(error) {
      if (signal.aborted) return;

      linkController.stop();
      dispatch(actions.affiliate.getLink.rejected(error));
    }

    api.affiliates
      .getLink()
      .then(onSuccess, onFail);
  };

  return reduction;
}

function* getLinkFulfilled(reduction, response) {
  return reduction.setIn(['affiliate', 'link'], response.referral_link);
}

function* getLinkRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* getPayout(reduction) {
  yield function effect(dispatch) {
    const { signal } = payoutController.start();

    function onSuccess(response) {
      payoutController.stop();
      dispatch(actions.affiliate.getPayout.fulfilled(response));
    }

    function onFail(error) {
      if (signal.aborted) return;

      payoutController.stop();
      dispatch(actions.affiliate.getPayout.rejected(error));
    }

    api.affiliates
      .getPayout()
      .then(onSuccess, onFail);
  };

  return reduction;
}

function* getPayoutFulfilled(reduction, response) {
  const formatters = {
    available_payout_amount: formatAmount,
    total_payout_amount: formatAmount,
  };

  return reduction.setIn(['affiliate', 'payout'], keysToCamelCase({ data: response, formatters, toImmutable: true }));
}

function* getPayoutRejected(reduction, error) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* requestPayout(reduction, payload) {
  yield function effect(dispatch) {
    function onSuccess() {
      dispatch(actions.affiliate.requestPayout.fulfilled());
      dispatch(actions.affiliate.getPayout());
    }

    function onError(error) {
      console.error(error);
      dispatch(actions.affiliate.requestPayout.rejected());
    }

    api.affiliates.requestPayout(payload).then(onSuccess, onError);
  };

  return reduction
    .deleteIn(['affiliate', 'modal', 'error'])
    .setIn(['affiliate', 'modal', 'processing'], true);
}

function* requestPayoutFulfilled(reduction) {
  if (getSize(reduction.get('affiliate'))) {
    yield function effect(dispatch) {
      dispatch(actions.affiliate.getPayout());
    };
  }

  return reduction.getIn(['affiliate', 'modal', 'type']) === MODALS.AFFILIATE.PAYOUT.REQUEST
    ? reduction.setIn(['affiliate', 'modal'], Map({ type: MODALS.AFFILIATE.PAYOUT.SUCCESS }))
    : reduction;
}

function* requestPayoutRejected(reduction) {
  return reduction.getIn(['affiliate', 'modal', 'type']) === MODALS.AFFILIATE.PAYOUT.REQUEST
    ? reduction
      .deleteIn(['affiliate', 'modal', 'processing'])
      .setIn(['affiliate', 'modal', 'error'], MESSAGE.FAIL.NETWORK_ERROR)
    : reduction;
}

function* sendApply(reduction, { companies, groups }) {
  yield function effect(dispatch) {
    function onSuccess() {
      dispatch(actions.user.getUserData());
      dispatch(actions.affiliate.sendApply.fulfilled());
    }

    function onError(error) {
      console.error(error);
      dispatch(actions.affiliate.sendApply.rejected());
    }

    const data = reduction.getIn(['affiliate', 'modal', 'data']);

    const payload = {
      channels: data.get('channels').toObject(),
      companies: [companies ? data.get('companies') : ''],
      groups: groups ? data.get('groups').filter(isTruthy).toArray() : [],
    };

    api.affiliates.sendApply(payload).then(onSuccess, onError);
  };

  return reduction
    .deleteIn(['affiliate', 'modal', 'error'])
    .setIn(['affiliate', 'modal', 'processing'], true);
}

function* sendApplyFulfilled(reduction) {
  if (!getSize(reduction.get('affiliate'))) return reduction;

  yield function effect(dispatch) {
    dispatch(actions.affiliate.getHistory());
    dispatch(actions.affiliate.getLink());
    dispatch(actions.affiliate.getPayout());
  };

  return reduction.setIn(['affiliate', 'modal'], Map({ type: MODALS.AFFILIATE.SURVEY.SUCCESS }));
}

function* sendApplyRejected(reduction) {
  return getSize(reduction.get('affiliate'))
    ? reduction
      .deleteIn(['affiliate', 'modal', 'processing'])
      .setIn(['affiliate', 'modal', 'error'], MESSAGE.FAIL.NETWORK_ERROR)
    : reduction;
}

function* setData(reduction, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['affiliate', ...path]
    : ['affiliate', path];

  if (value !== undefined) return reduction.setIn(fullPath, value);

  let state = reduction.deleteIn(fullPath);

  fullPath.pop();

  while (getSize(fullPath) > 1) {
    if (!getSize(state.getIn(fullPath))) {
      state = state.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return state;
}

const affiliate = {
  [actions.affiliate.bootstrap]: bootstrap,
  [actions.affiliate.cleanUp]: cleanUp,
  [actions.affiliate.getHistory]: getHistory,
  [actions.affiliate.getHistory.fulfilled]: getHistoryFulfilled,
  [actions.affiliate.getHistory.rejected]: getHistoryRejected,
  [actions.affiliate.getLink]: getLink,
  [actions.affiliate.getLink.fulfilled]: getLinkFulfilled,
  [actions.affiliate.getLink.rejected]: getLinkRejected,
  [actions.affiliate.getPayout]: getPayout,
  [actions.affiliate.getPayout.fulfilled]: getPayoutFulfilled,
  [actions.affiliate.getPayout.rejected]: getPayoutRejected,
  [actions.affiliate.requestPayout]: requestPayout,
  [actions.affiliate.requestPayout.fulfilled]: requestPayoutFulfilled,
  [actions.affiliate.requestPayout.rejected]: requestPayoutRejected,
  [actions.affiliate.sendApply]: sendApply,
  [actions.affiliate.sendApply.fulfilled]: sendApplyFulfilled,
  [actions.affiliate.sendApply.rejected]: sendApplyRejected,
  [actions.affiliate.setData]: setData,
};

export default affiliate;
